import { onSensitiveMemoryCacheClear } from './sensitive-cache-lifecycle';
import type { ResourceScope } from '@kop/contracts';
import type { AuthenticatedSession } from '../auth/types';
import { headquartersSiteChoices } from '../headquarters-site-navigation';

let generation = 0;
onSensitiveMemoryCacheClear(() => { generation += 1; });

export type HeadquartersReadContext = Readonly<{ uid: string; siteId: string; generation: number }>;

export function createHeadquartersReadContext(uid: string, siteId: string): HeadquartersReadContext | null {
  if (!uid || !/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u.test(siteId)) return null;
  return { uid, siteId, generation };
}

/** Navigation preference only; the destination must independently verify site authority. */
export function headquartersReadSite(context: HeadquartersReadContext | null | undefined, uid: string, sites: readonly { siteId: string }[]): string | null {
  if (!context || !uid || context.uid !== uid || context.generation !== generation) return null;
  return sites.some(site => site.siteId === context.siteId) ? context.siteId : null;
}

/** Selects an unchanged server-issued scope; a navigation preference cannot invent site authority. */
export function headquartersFeatureScope(session: AuthenticatedSession, context: HeadquartersReadContext | null) {
  const active = session.scopes.find(scope => scope.id === session.activeScopeId) ?? session.scopes[0]!;
  if (active.resource.kind !== 'tenant') return active;
  const choices = headquartersSiteChoices(session);
  const sites = choices.flatMap(scope => scope.resource.kind === 'site' ? [{ siteId: scope.resource.siteId }] : []);
  const selected = headquartersReadSite(context, session.identity.id, sites);
  return choices.find(scope => scope.resource.kind === 'site' && scope.resource.siteId === selected) ?? active;
}

/** Carries a navigation preference only; the server independently authorizes every read. */
export function headquartersWorkScope(resource: ResourceScope, role: string, uid: string, context: HeadquartersReadContext | null): ResourceScope {
  if (resource.kind !== 'tenant' || !['hqSafety', 'hqAudit'].includes(role)
    || !uid || !context || context.uid !== uid || context.generation !== generation
    || !/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u.test(context.siteId)) return resource;
  return { kind: 'site', tenantId: resource.tenantId, siteId: context.siteId };
}
